<template>
  <div class="pending-widget">
    <Pending
      ref="actions"
      :widget-impact-range="pendOptImpactRange"
      :widget-sort-by="pendOptSortBy"
      :widget-leaders="pendOptLeader"
      as-widget
    />
  </div>
</template>

<script>
/**
 * @typedef {Object} SortBy
 * @property {number} value - Type of sorting. Must be between 0 and 3 included.
 *                            0 - Sort by title
 *                            1 - Sort by entry date
 *                            2 - Sort by deadline date
 *                            3 - Sort by impact value
 * @property {boolean} [desc] - Sort by descending order (ascending otherwise)
 *
 * @typedef {Object} ImpactRange
 * @property {number} min - Minimum impact value
 * @property {number} max - Maximum impact value
 */
/**
 * Pending action list widget
 * Allow the user to see pending actions in a custom filtred list
 *
 * @vue-prop {SortBy} sortBy - Type of data to sort by, can contains sort order
 * @vue-prop {Array} [leader] - Only actions with leader ID in this list will be displayed
 *                              0 in the array will filter in unspecified leader
 *                              Empty list will disable the filter
 * @vue-prop {ImpactRange} [impactRange] - Action within the range will be filtered in
 */

// Import components
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinPendingOptions from '@/mixins/page-options/pending.vue';
import Pending from '@/views/dmp/pending.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Utils
import { convertImpactFilterToArray, parseImpactFilterParam } from '@/utils/problem-utils';

export default {

  name: 'PendingWidget',

  components: {
    Pending,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinPendingOptions, mixinSocketHandler],

  props: {
    sortBy: {
      type: Object,
      default: () => ({ value: 1, desc: false }),
      validator: v => v.value >= 0 && v.value <= 3,
    },
    leader: {
      type: Array,
      default: () => [],
    },
    impactRange: {
      type: [Object, Array],
      default: () => ({ min: 0, max: 3 }),
    },
  },

  computed: {
    config() {
      let config = this.cfgTemplateEdition
        ? this.pendOptions.filter(opt => !['leaderTitle', 'leader'].includes(opt.key))
        : this.pendOptions;
      const excludeKeys = ['displayNumberOfPendingActionsTitle', 'displayNumberOfPendingActions'];
      config = config.filter(opt => !excludeKeys.includes(opt.key))
      config.push(this.defaultBgColorConfig);
      return config;
    },
    // ========= Config options =========
    pendOptSortBy() {
      return this.sortBy;
    },
    pendOptLeader() {
      return this.leader;
    },
    pendOptImpactRange() {
      return convertImpactFilterToArray(this.impactRange);
    },
  },

  watch: {
    sortBy() {
      this.$refs.actions.refresh(true);
    },
    leader() {
      this.$refs.actions.refresh(true);
    },
    impactRange() {
      this.$refs.actions.refresh(true);
    },
  },

  methods: {
    sizeChanged() {
      if (this.$refs.actions) {
        this.$refs.actions.onSizeChanged(true);
      }
    },
  },
};
</script>

<style lang="scss">
.pending-widget {
  height: 100%;
  max-height: 100%;
  max-width: 100%;
}

.pending-widget__list {
  box-sizing: border-box;
  max-height: 100%;
  max-width: 100%;
  padding: 6px;
}

.pending-widget__loading {
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2),
              0px 2px 2px 0px rgba(0,0,0,0.14),
              0px 1px 5px 0px rgba(0,0,0,0.12);
  padding: 10px 0;
  font-weight: 700;
}

/** Custom ActionItem style */
.pending-widget {
  .action-item {
    border-radius: 2px;
    box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2),
                0px 2px 2px 0px rgba(0,0,0,0.14),
                0px 1px 5px 0px rgba(0,0,0,0.12);
  }
}
</style>
